import axios from 'axios';
import { TokenLimits, CreditLimits, AuditLogFilters } from '../types';
import { encryptData } from '../utils/encryption';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token refresh state
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

// Helper function to check if token is expired or about to expire
const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    // Check if token expires in the next 5 minutes (300 seconds)
    return payload.exp < (currentTime + 300);
  } catch (error) {
    return true; // If we can't parse the token, consider it expired
  }
};

// Helper function to refresh the token
const refreshAuthToken = async (): Promise<string> => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  try {
    const response = await axios.post(`${API_URL}/auth/refresh-token`, {}, {
      headers: {
        'Authorization': `Bearer ${refreshToken}`,
        'Content-Type': 'application/json',
      },
    });

    const { access_token, refresh_token } = response.data;
    
    // Update stored tokens
    localStorage.setItem('token', access_token);
    if (refresh_token) {
      localStorage.setItem('refreshToken', refresh_token);
    }

    return access_token;
  } catch (error) {
    // If refresh fails, clear all tokens and redirect to login
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    window.location.href = '/login';
    throw error;
  }
};

// Add request interceptor to add auth token and handle token refresh
api.interceptors.request.use(
  async (config) => {
    let token = localStorage.getItem('token');
    
    if (token) {
      // Check if token is expired or about to expire
      if (isTokenExpired(token)) {
        if (isRefreshing) {
          // If already refreshing, wait for it to complete
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(() => {
            const newToken = localStorage.getItem('token');
            if (newToken && config.headers) {
              config.headers.Authorization = `Bearer ${newToken}`;
            }
            return config;
          });
        }

        // Start refreshing
        isRefreshing = true;
        
        try {
          const newToken = await refreshAuthToken();
          processQueue(null, newToken);
          token = newToken;
        } catch (error) {
          processQueue(error, null);
          throw error;
        } finally {
          isRefreshing = false;
        }
      }

      if (config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, wait for it to complete
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          const token = localStorage.getItem('token');
          if (token) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return api(originalRequest);
        }).catch((err) => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAuthToken();
        processQueue(null, newToken);
        
        // Retry the original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        
        // Clear tokens and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const authApi = {
  login: async (email: string, password: string) => {
    const formData = new URLSearchParams();
    formData.append('username', email.toLowerCase());
    formData.append('password', encryptData(password));

    const response = await api.post('/auth/login', formData.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    if (response.data.access_token) {
      // Store token and user data
      localStorage.setItem('token', response.data.access_token);
      if (response.data.refresh_token) {
        localStorage.setItem('refreshToken', response.data.refresh_token);
      }
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
    }

    return response;
  },

  register: async (data: any) => {
    // Extract token limits from settings if present
    const { settings, password, email, ...userData } = data;
    const payload = {
      ...userData,
      email: email ? email.toLowerCase() : undefined,
      password: password ? encryptData(password) : undefined,
      input_token_limit: settings?.input_token_limit,
      output_token_limit: settings?.output_token_limit,
      access_type: settings?.access_type,
    };
    return api.post('/auth/register', payload);
  },

  getCurrentUser: async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No token found');
    }

    return api.get('/auth/me', {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      // Always clear all stored data
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    }
  },

  requestPasswordReset: async (email: string) => {
    return api.post('/auth/password-reset', { 
      email: email.toLowerCase(),
      callback_url: `${window.location.origin}/reset-password`
    });
  },

  confirmPasswordReset: async (token: string, password: string, confirmPassword: string) => {
    return api.post('/auth/password-reset/confirm', {
      token,
      new_password: encryptData(password),
      confirm_password: encryptData(confirmPassword),
    });
  },

  refreshToken: async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await api.post('/auth/refresh-token', {}, {
      headers: {
        'Authorization': `Bearer ${refreshToken}`,
      },
    });

    if (response.data.access_token) {
      // Store new tokens
      localStorage.setItem('token', response.data.access_token);
      if (response.data.refresh_token) {
        localStorage.setItem('refreshToken', response.data.refresh_token);
      }
    }

    return response;
  },
};

export interface FetchTasksParams {
  page?: number;
  limit?: number;
  session_id?: string;
  task_name?: string;
  start_timestamp?: number;
  end_timestamp?: number;
}

export const usersApi = {
  getUsers: async (params: any) => {
    return api.get('/users', { params });
  },

  getUser: async (id: number) => {
    return api.get(`/users/${id}`);
  },

  createUser: async (data: any) => {
    const { password, email, ...rest } = data;
    return api.post('/users/', {
      ...rest,
      email: email ? email.toLowerCase() : undefined,
      password: password ? encryptData(password) : undefined
    });
  },

  updateUser: async (id: number, data: any) => {
    const { settings, organization_id, role, password, current_password, ...userData } = data;
    const payload = {
      ...userData,
      input_token_limit: settings?.input_token_limit,
      output_token_limit: settings?.output_token_limit,
      access_type: settings?.access_type,
      organization_id: organization_id,
      organization_role: role === 'org_admin' ? 'admin' : 'member'
    };
    if (password) {
      payload.password = encryptData(password);
    }
    if (current_password) {
      payload.current_password = encryptData(current_password);
    }
    return api.put(`/users/${id}/complete`, payload);
  },

  updateUserComplete: async (id: number, data: any) => {
    const { password, current_password, ...userData } = data;
    const payload = {
      ...userData,
    };
    if (password) {
      payload.password = encryptData(password);
    }
    if (current_password) {
      payload.current_password = encryptData(current_password);
    }
    return api.put(`/users/${id}/complete`, payload);
  },

  updateUserStatus: async (id: number, status: string) => {
    return api.put(`/users/${id}/status`, { status });
  },

  updateUserLimits: async (userId: number, limits: TokenLimits) => {
    return api.put(`/tokens/users/${userId}/limits`, limits);
  },

  updateUserCreditLimits: async (userId: number, limits: CreditLimits) => {
    return api.put(`/credits/users/${userId}/limits`, limits);
  },

  deleteUser: async (userId: number) => {
    return api.delete(`/users/${userId}`);
  },

  resetUserPassword: async (userId: number, newPassword: string) => {
    return api.post(`/users/${userId}/reset-password`, null, {
      params: {
        new_password: encryptData(newPassword)
      }
    });
  },

  getUserLimits: async (userId: number) => {
    return api.get(`/tokens/users/${userId}/limits`);
  },

  getUserCreditLimits: async (userId: number) => {
    return api.get(`/credits/users/${userId}/limits`);
  },

  getUserCreditHistory: async (userId: number, limit: number = 50, skip: number = 0) => {
    return api.get(`/credits/users/${userId}/history`, { params: { limit, skip } });
  },

  getUserOrganizations: async (userId: number) => {
    return api.get(`/users/${userId}/organizations`);
  },

  fetchUserTasks: async (userId: number, params: FetchTasksParams & { task_type?: 'web' | 'ide' }) => {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.set('skip', String((params.page - 1) * (params.limit || 100)));
    if (params.limit) queryParams.set('limit', String(params.limit));
    if (params.session_id) queryParams.set('session_id', params.session_id);
    if (params.task_name) queryParams.set('task_name', params.task_name);
    if (params.start_timestamp) queryParams.set('start_timestamp', String(params.start_timestamp));
    if (params.end_timestamp) queryParams.set('end_timestamp', String(params.end_timestamp));
    if (params.task_type) queryParams.set('task_type', params.task_type);

    const response = await api.get(`/tasks/${userId}?${queryParams.toString()}`);
    return response.data;
  },

  getAvailableOrganizations: async (userId: number, params?: any) => {
    return api.get(`/users/${userId}/available-organizations`, { params });
  },

  getAllOrganizations: async (params?: any) => {
    return api.get('/organizations', { params });
  },

  updateUserOrganization: async (userId: number, orgId: number, role: string) => {
    return api.put(`/users/${userId}/organizations/${orgId}`, { role });
  },

  removeUserFromOrganization: async (userId: number, orgId: number) => {
    return api.delete(`/users/${userId}/organizations/${orgId}`);
  },
};

export const organizationsApi = {
  getOrganizations: async (params: any) => {
    return api.get('/organizations', { params });
  },

  getOrganization: async (id: number) => {
    return api.get(`/organizations/${id}`);
  },

  createOrganization: async (data: any) => {
    // Extract token limits from settings if present
    const { settings, status, members, ...orgData } = data;
    const payload = {
      ...orgData,
      input_token_limit: settings?.input_token_limit,
      output_token_limit: settings?.output_token_limit,
      access_type: settings?.access_type,
    };
    return api.post('/organizations/', payload);
  },

  updateOrganization: async (id: number, data: any) => {
    // Format data for complete update
    const payload = {
      ...data,
      settings: {},
      members: [] // TODO: Add members management
    };
    return api.put(`/organizations/${id}/complete`, payload);
  },

  inviteMember: async (orgId: number, data: any) => {
    return api.post(`/organizations/${orgId}/invite`, data);
  },

  updateOrganizationLimits: async (orgId: number, limits: TokenLimits) => {
    return api.put(`/tokens/organizations/${orgId}/limits`, limits);
  },

  updateOrganizationCreditLimits: async (orgId: number, limits: CreditLimits) => {
    return api.put(`/credits/organizations/${orgId}/limits`, limits);
  },

  getOrganizationLimits: async (orgId: number) => {
    return api.get(`/tokens/organizations/${orgId}/limits`);
  },

  getOrganizationCreditLimits: async (orgId: number) => {
    return api.get(`/credits/organizations/${orgId}/limits`);
  },
};

export const auditApi = {
  getAuditLogs: async (filters?: AuditLogFilters) => {
    return api.get('/audit-logs', { params: filters });
  },

  getAllAuditLogs: async (filters?: Omit<AuditLogFilters, 'skip' | 'limit'>) => {
    return api.get('/audit-logs', { 
      params: { 
        ...filters, 
        limit: 10000 // Large limit to get all logs
      } 
    });
  },

  getAuditLog: async (logId: number) => {
    return api.get(`/audit-logs/${logId}`);
  },

  getUserActivity: async (userId: number, params?: { skip?: number; limit?: number }) => {
    return api.get(`/users/${userId}/activity`, { params });
  }
};

export const trialsApi = {
  getTrialActivities: async (params?: {
    skip?: number;
    limit?: number;
    app_type?: string;
    activity_type?: string;
    trial_status?: string;
    user_id?: number;
    organization_id?: number;
    start_date?: string;
    end_date?: string;
  }) => {
    return api.get('/trials/activities', { params });
  },

  getUserTrialSummary: async (userId: number) => {
    return api.get(`/trials/users/${userId}/summary`);
  },

  getTrialAnalytics: async (params?: {
    days?: number;
    app_type?: string;
    organization_id?: number;
  }) => {
    return api.get('/trials/analytics', { params });
  },

  getTrialStats: async (params?: {
    skip?: number;
    limit?: number;
    app_type?: string;
    user_id?: number;
    organization_id?: number;
    start_date?: string;
    end_date?: string;
  }) => {
    return api.get('/trials/stats', { params });
  }
};

export const dashboardApi = {
  // Super Admin Endpoints
  getSuperAdminIDEStats: async (params?: { start_date?: string; end_date?: string; org_id?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.set('start_date', params.start_date);
    if (params?.end_date) queryParams.set('end_date', params.end_date);
    if (params?.org_id) queryParams.set('org_id', params.org_id);
    return api.get(`/dashboard/super-admin/ide-stats?${queryParams.toString()}`);
  },

  getSuperAdminWebStats: async (params?: { start_date?: string; end_date?: string; org_id?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.set('start_date', params.start_date);
    if (params?.end_date) queryParams.set('end_date', params.end_date);
    if (params?.org_id) queryParams.set('org_id', params.org_id);
    return api.get(`/dashboard/super-admin/web-stats?${queryParams.toString()}`);
  },

  // Org Admin Endpoints
  getOrgAdminIDEStats: async (params?: { start_date?: string; end_date?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.set('start_date', params.start_date);
    if (params?.end_date) queryParams.set('end_date', params.end_date);
    return api.get(`/dashboard/org-admin/ide-stats?${queryParams.toString()}`);
  },

  getOrgAdminWebStats: async (params?: { start_date?: string; end_date?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.set('start_date', params.start_date);
    if (params?.end_date) queryParams.set('end_date', params.end_date);
    return api.get(`/dashboard/org-admin/web-stats?${queryParams.toString()}`);
  },

  // Additional Stats Endpoints
  getUserStats: async (days: number = 30) => {
    return api.get('/dashboard/user-stats', { params: { days } });
  },

  getOrgStats: async () => {
    return api.get('/dashboard/org-stats');
  },
};

export const billingApi = {
  getOrganizationBilling: async (orgId: number) => {
    return api.get(`/billing/organizations/${orgId}/billing`);
  },

  updateOrganizationBilling: async (orgId: number, data: {
    billing_start_date?: string;
    billing_cycle_day?: number;
  }) => {
    return api.put(`/billing/organizations/${orgId}/billing`, data);
  },

  resetOrganizationCredits: async (orgId: number) => {
    return api.post(`/billing/organizations/${orgId}/reset-credits`);
  },

  getMonthlyUsageHistory: async (orgId: number, months: number = 12) => {
    return api.get(`/billing/organizations/${orgId}/monthly-usage`, {
      params: { months }
    });
  },

  getUsageChart: async (orgId: number, period: string = "6m") => {
    return api.get(`/billing/organizations/${orgId}/usage-chart`, {
      params: { period }
    });
  },

  getMonthUserUsage: async (orgId: number, monthYear: string) => {
    return api.get(`/billing/organizations/${orgId}/month/${monthYear}/users`);
  },

  getUserMonthlyUsage: async (userId: number, months: number = 12) => {
    return api.get(`/billing/users/${userId}/monthly-usage`, {
      params: { months }
    });
  },

  resetAllCredits: async () => {
    return api.post('/billing/reset-all-credits');
  }
};

export default api;
